import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Typography,
    Collapse,
    Avatar,
    Tooltip,
    useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    BorderAll,
    DisplaySettings,
    ExpandLess,
    ExpandMore,
    Opacity,
    Chat,
} from '@mui/icons-material';

import { logout } from '../reducers/authReducer';
import { clearMessages, selectChatContext } from '../reducers/messagesReducer';
import { setLoading } from '../reducers/loadingReducer';
import { showToast, hideToast } from '../reducers/uiReducer';
import {
    setShowTiles,
    setIsActiveChat,
    setSelectedTileId,
    clearSelectedTileId,
    addPromptItem,
    removePromptItem,
    updatePromptItem,
    clearPromptItems,
    setSelectedPromptItem,
    clearSelectedPromptItem,
} from '../reducers/uiReducer';
import { clearPromptBar, setPromptBarActive } from '../reducers/promptBarReducer';
import { clearSelectedDocument } from '../reducers/documentsReducer';
import { clearSelectedDataset } from '../reducers/datasetsReducer';
import { clearSelectedPrompt } from '../reducers/promptsReducer';
import { menuConfig } from '../config/menuConfig';
import { RackletLogoMini } from '../assets/SvgIcons';
import { checkUserRole } from '../services/checkUserRole';

const MenuDrawer = ({ drawerOpen, toggleDrawer }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const [openSubMenu, setOpenSubMenu] = useState({});

    if (user) {
        menuConfig.header.profileName = `${user.firstname} ${user.lastname.charAt(0)}`;
        menuConfig.header.profileEmail = user.email;
    }

    const role = checkUserRole();

    const handleLogout = () => {
        dispatch(logout());
    };

    const handleNewChat = () => {
        dispatch(clearMessages());
        dispatch(clearSelectedTileId());
        dispatch(setShowTiles(false));
        dispatch(setIsActiveChat(true));
        dispatch(setPromptBarActive(true));
        dispatch(clearSelectedDocument());
        dispatch(clearSelectedDataset());
        dispatch(clearSelectedPromptItem());
        dispatch(clearPromptItems());
        toggleDrawer(false);
        navigate('/');
    };

    const handleToggleSubMenu = (index) => {
        setOpenSubMenu((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const getEnvironment = () => process.env.REACT_APP_ENVIRONMENT || 'DEV';

    const renderEnvironment = (env) => {
        return (
            <Box sx={{
                height: 56,
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {env === 'PROD' ? (
                    <Button
                        onClick={handleNewChat}
                        sx={{ width: '100%', height: '100%', textTransform: 'none' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 6,
                                    background: theme.palette.background.paper,
                                    height: 40,
                                    borderRadius: 1,
                                    boxShadow: theme.shadows[2],
                                }}
                            >
                                <RackletLogoMini />
                            </Box>
                            <Typography sx={{ fontSize: 21, fontFamily: 'Arial' }}>
                                Racklet
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    backgroundColor: '#66B2FF',
                                    color: '#fff',
                                    borderRadius: '135px',
                                    ml: 1,
                                    px: 1,
                                }}
                            >
                                AI
                            </Typography>
                        </Box>
                    </Button>
                ) : (
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {env}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Drawer anchor="left" open={drawerOpen} onClose={(e) => toggleDrawer(false)(e)}>
            <Box
                sx={{
                    width: 270,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(to top, #1c1c1e, #2c2c2e, #3a3a3c)'
                        : 'linear-gradient(to top, #ffffff, rgb(238, 241, 247), #d1d5db)',
                    height: '100%',
                    position: 'relative',
                }}
            >
                {renderEnvironment(getEnvironment())}

                <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 25, height: 25 }}>
                        {menuConfig.header.profileName.charAt(0)}
                    </Avatar>
                    <Typography sx={{ ml: 1 }}>{menuConfig.header.profileName}</Typography>
                    <Tooltip title="Logout">
                        <Button
                            variant="outlined"
                            onClick={handleLogout}
                            sx={{ ml: 'auto', minWidth: 0, px: 1 }}
                        >
                            <menuConfig.header.logoutButton.icon />
                        </Button>
                    </Tooltip>
                </Box>

                <Divider />

                <List>
                    {menuConfig.mainMenu
                        .filter((item) => role === 'admin' || item.role === role)
                        .map((item, index) => (
                            <React.Fragment key={index}>
                                <ListItem
                                    button
                                    disabled={item.disabled}
                                    onClick={() => {
                                        if (!item.disabled) {
                                            if (item.link) {
                                                navigate(item.link);
                                            } else if (item.subMenu) {
                                                handleToggleSubMenu(index);
                                            }
                                        }
                                    }}
                                    sx={{
                                        opacity: item.disabled ? 0.5 : 1,
                                        cursor: item.disabled ? 'default' : 'pointer',
                                        '&:hover': {
                                            backgroundColor: item.disabled ? 'inherit' : theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    {item.icon && <ListItemIcon><item.icon /></ListItemIcon>}
                                    <ListItemText
                                        primary={
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                fontSize: '1rem',
                                                color: item.disabled
                                                    ? theme.palette.text.disabled
                                                    : theme.palette.text.primary,
                                            }}>
                                                {item.text}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {item.subMenu && (
                                    <Collapse in={openSubMenu[index]} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {item.subMenu.map((subItem, subIndex) => (
                                                <ListItem
                                                    key={subIndex}
                                                    button
                                                    disabled={subItem.disabled}
                                                    onClick={() => {
                                                        if (!subItem.disabled) {
                                                            navigate(subItem.link);
                                                        }
                                                    }}
                                                    sx={{
                                                        pl: 4,
                                                        opacity: subItem.disabled ? 0.5 : 1,
                                                        '&:hover': {
                                                            backgroundColor: subItem.disabled ? 'inherit' : theme.palette.action.hover,
                                                        },
                                                    }}
                                                >
                                                    {subItem.icon && <ListItemIcon><subItem.icon /></ListItemIcon>}
                                                    <ListItemText
                                                        primary={
                                                            <Typography sx={{
                                                                fontWeight: 'bold',
                                                                fontSize: '0.9rem',
                                                                color: subItem.disabled
                                                                    ? theme.palette.text.disabled
                                                                    : theme.palette.text.primary,
                                                            }}>
                                                                {subItem.text}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                )}
                                {item.text === 'CHAT HISTORY' && !item.subMenu && (
                                    <Box
                                        sx={{
                                            height: 165,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: theme.palette.background.default,
                                            p: 2,
                                            m: 1,
                                            color: theme.palette.text.secondary,
                                            borderRadius: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Chat sx={{ fontSize: '3rem' }} />
                                        <Typography>No chat history available</Typography>
                                    </Box>
                                )}
                            </React.Fragment>
                        ))}
                </List>

                <Divider />
                <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2, textAlign: 'center' }}>
                    <Typography variant="body2">{menuConfig.footer.supportText}</Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default MenuDrawer;
